import {
  Box,
  Card,
  CardContent,
  Typography,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
} from "recharts";
import React, { useState } from "react";

// Interface for revenue trend data
interface RevenueTrendData {
  month: string;
  year: number;
  flightRevenue: number;
  rideRevenue: number;
  total: number;
}

interface RevenueChartsProps {
  revenueTrend?: RevenueTrendData[];
}

const CustomTooltip: React.FC<TooltipProps<number, string>> = ({
  active,
  payload,
}) => {
  if (active && payload && payload.length) {
    const value = payload[0].value as number;
    const formattedValue = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);

    return (
      <Box
        sx={{
          backgroundColor: "#fff",
          border: "1px solid #E5E7EB",
          borderRadius: "6px",
          padding: "8px 12px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <Typography sx={{ fontSize: "12px", color: "#6B7280", mb: 0.5 }}>
          Revenue
        </Typography>
        <Typography
          sx={{ fontSize: "14px", fontWeight: 600, color: "#101828" }}
        >
          {formattedValue}
        </Typography>
      </Box>
    );
  }
  return null;
};

const RevenueTrends: React.FC<RevenueChartsProps> = ({ revenueTrend = [] }) => {
  const formatYAxis = (value: number) => {
    if (value >= 1000000) return `${value / 1000000}m`;
    if (value >= 1000) return `${value / 1000}k`;
    return value.toString();
  };

  // Filter data based on selected year
  const filteredData = revenueTrend.map((item) => ({
    month: item.month.substring(0, 3), // Use first 3 letters of month
    value: item.total,
    flightRevenue: item.flightRevenue,
    rideRevenue: item.rideRevenue,
  }));

  // Get available years from the data
  const availableYears = [
    ...new Set(revenueTrend.map((item) => item.year.toString())),
  ];

  return (
    <Card
      sx={{
        padding: 0,
        borderRadius: "8px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        "&:hover": {
          boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
        },
        mt: 5,
      }}
    >
      <CardContent sx={{ padding: 3, overflowX: "auto" }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography
            sx={{ fontSize: "18px", fontWeight: "600", color: "#000" }}
          >
            Revenue Trends
          </Typography>

          {/* <FormControl size="small" sx={{ minWidth: 100 }}>
            <Select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              sx={{
                fontSize: "14px",
                color: "#111",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#E5E7EB",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#D1D5DB",
                },
              }}
            >
              {availableYears.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl> */}
        </Box>

        <Box minWidth={{ xs: "600px", sm: "auto" }} height={400}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={filteredData}
              margin={{ top: 10, right: 0, bottom: 10, left: 0 }}
            >
              <CartesianGrid
                strokeDasharray="0"
                stroke="#F3F4F6"
                vertical={false}
              />
              <XAxis
                dataKey="month"
                axisLine={{ stroke: "#E5E7EB" }}
                tickLine={{ stroke: "#E5E7EB" }}
                tick={{ fill: "#000", fontSize: 12 }}
              />
              <YAxis
                tickFormatter={formatYAxis}
                axisLine={{ stroke: "#E5E7EB" }}
                tickLine={{ stroke: "#E5E7EB" }}
                tick={{ fill: "#000", fontSize: 12 }}
              />
              <Tooltip
                content={<CustomTooltip />}
                cursor={{ fill: "rgba(16, 185, 129, 0.1)" }}
              />

              <Bar
                dataKey="value"
                fill="#379E66"
                radius={[4, 4, 0, 0]}
                maxBarSize={50}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
};

export default RevenueTrends;
